<script setup lang="ts">
  import { computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import certificateIcon from '@/shared/assets/icons/CertificateIcon.svg';
  import { useDashboardStore } from '../../model/dashboard.store';
  import { formatVariationPct } from '../../model/format';
  import { formatPeriodLabel } from '../../model/period';
  import HorizontalBarList, { type HorizontalBarItem } from '../HorizontalBarList.vue';
  import VariationBadge from '../VariationBadge.vue';
  import WidgetCard from '../WidgetCard.vue';

  const dashboardStore = useDashboardStore();
  const { data, isLoading, comparisonLabel, granularity, period } = storeToRefs(dashboardStore);

  const periodLabel = computed(() => formatPeriodLabel(granularity.value, period.value));

  const items = computed<readonly HorizontalBarItem[]>(
    () => data.value?.trainingTypes.items ?? []
  );

  const hasData = computed(() => items.value.some((item) => item.value > 0));

  const variationPct = computed(() =>
    hasData.value ? (data.value?.trainingTypes.variationPct ?? null) : null
  );

  const badgeText = computed(() => {
    if (variationPct.value === null || variationPct.value === 0) return undefined;
    const direction = variationPct.value > 0 ? 'mais' : 'menos';
    return `${formatVariationPct(variationPct.value)} ${direction} treinamentos`;
  });
</script>

<template>
  <WidgetCard
    title="Tipos de Treinamento"
    :icon="certificateIcon"
    :period-label="periodLabel"
    :loading="isLoading"
    :has-data="data !== null"
    skeleton="bar-list"
  >
    <div
      data-testid="training-types-widget"
      class="flex h-full flex-col gap-5"
    >
      <div class="flex min-h-0 flex-1 items-center">
        <p
          v-if="items.length === 0"
          data-testid="training-types-empty"
          class="w-full text-center text-sm text-gray-03"
        >
          Nenhum treinamento no período
        </p>
        <HorizontalBarList
          v-else
          class="max-h-full w-full"
          :items="items"
        />
      </div>
      <VariationBadge
        :variation-pct="variationPct"
        :text="badgeText"
        :comparison-label="comparisonLabel"
      />
    </div>
  </WidgetCard>
</template>
